import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import AddButton from '../../../components/common/AddButton/AddButton';
import Sort from '../../../components/common/Sort/Sort';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import SimpleSearchBox from '../../../components/common/Search/SimpleSearch';

import useFilterStore from '../../../store/useFilterStore';
import useRefreshStore from '../../../store/useRefreshStore';

import './ReleaseNotes.css';
import { fetchReleaseNotes, deleteReleaseNote } from '../../../services/ReleaseNotes/Releasenotesapi';
import Filter from '../../../components/common/Filter/Filter';
import DeleteIcon from '../../../components/Table/DeleteIcon/DeleteIcon';
import DeletePopup from '../../../components/Popup/DeletePopup/DeletePopup';
import { showAlert } from '../../../components/common/ExportLabel/Exporterror';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';

const pageKey = "release_notes";

const ReleaseNotes = () => {
  const navigate = useNavigate();

  // ── Filter / sort / search persistence (same as OurPlans) ────────────────────
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);

  const [items, setItems] = useState([]);

  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: "" });
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || "Newest");

  const { refreshKeys } = useRefreshStore();
  const refreshKey = refreshKeys["Release Notes"] || 0;

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [selectedFilterOption, setSelectedFilterOption] = useState(
    savedFilters?.selectedFilterOption || "All"
  );

  const sortOptions = ['Newest', 'Oldest', 'A-Z', 'Z-A'];

  const handleFilterOptionSelect = (option) => setSelectedFilterOption(option);
  const statusFilterOptions = [
    { value: 'All', label: 'All' },
    { value: 'published', label: 'Published' },
    { value: 'draft', label: 'Draft' },
  ];

  // ── Fetch on mount + whenever search / filter / sort changes ─────────────────
  useEffect(() => {
    fetchReleaseNotesList();
    setFilters(pageKey, { searchParams, selectedFilterOption, selectedSortOption });
  }, [searchParams, selectedSortOption, selectedFilterOption]);

  // ── Re-fetch when refresh store triggers ─────────────────────────────────────
  useEffect(() => {
    fetchReleaseNotesList();
  }, [refreshKey]);

  // ─── Fetch (list or single item) — same signature as OurPlans.fetchPlans ─────
  const fetchReleaseNotesList = async () => {
    try {
      const data = await fetchReleaseNotes({
        search: searchParams.term,
        filter: selectedFilterOption === 'All' ? undefined : selectedFilterOption,
        sort: mapSortOptionToQuery(selectedSortOption),
      });
      setItems(data);
    } catch (error) {
      // plug in your showAlert component here if you have one
      console.error(error.message);
    }
  };





  // ─── Sort helper ──────────────────────────────────────────────────────────────
  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z': return 'a_to_z';
      case 'Z-A': return 'z_to_a';
      case 'Newest': return 'newest';
      case 'Oldest': return 'oldest';
      default: return 'newest';
    }
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────────
  const handleNewClick = () => navigate('/release-notes/create');
  const handleSearchSelect = (term) => setSearchParams((prev) => ({ ...prev, term }));
  const handleSortOptionSelect = (option) => setSelectedSortOption(option);



  const handleDetailsClick = (item, id) => {
    navigate(`/release-notes/detail/${id}`, { state: { note: item } });
  };

  const formatDate = (date) => {
    if (!date) return '—';

    const d = new Date(date);

    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleDeleteClick = (e, item) => {
    e.stopPropagation(); // prevent row click navigating to detail
    setSelectedItem(item);
    setIsDeletePopupOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteReleaseNote(selectedItem.id);
      setIsDeletePopupOpen(false);
      setSelectedItem(null);
      fetchReleaseNotesList();
      SuccessAlert("Release note deleted successfully.");
    } catch (error) {
      showAlert(error.message, 'error');
      console.error(error.message);
    }
  };


  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="whole-container">

      <div className="top-heading">
        <PageHeader pageName="Engagement /" nestedpageName="Release Notes" linkTo="" />
        <div className="export-container">
        </div>
      </div>

      <div className="pagename-container">
        <div className="page-name">
          <h1 className="common-text fts-22 ftw-6 blue">Release Notes</h1>
        </div>
        <div className="pagename-right-container">
          <AddButton onClick={handleNewClick} />
          <SimpleSearchBox
            placeholder="Search Release Notes"
            onSearchSelect={handleSearchSelect}
            defaultValue={searchParams?.term}
          />
          <Filter
            filterOptions={statusFilterOptions}
            onFilterSelect={handleFilterOptionSelect}
            defaultValue={selectedFilterOption}
          />
          <Sort
            options={sortOptions}
            onOptionSelect={handleSortOptionSelect}
            defaultValue={selectedSortOption}
          />
        </div>
      </div>

      <div className="table-container">
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th className="d-flex ai-center release-list">
                VERSION
              </th>
              <th>TITLE</th>
              <th>NEW FEATURES</th>
              <th>IMPROVEMENTS</th>
              <th>BUG FIXES</th>
              <th>RELEASE DATE</th>
              <th>STATUS</th>
              <th></th>
            </tr>
          </thead>

          <tbody className="common-table-tbody">
            {items.map((release) => (
              <tr
                key={release.id}
                onClick={() => handleDetailsClick(release, release.id)}
                className={release.is_archived ? 'archived-item' : ''}
                style={{ cursor: "pointer" }}
              >
                <td className="d-flex ai-center release-list">
                  {release.version}
                </td>
                <td className='w-30'>{release.title}</td>
                <td>{release.features?.length ?? 0}</td>
                <td>{release.improvements?.length ?? 0}</td>
                <td>{release.bug_fixes?.length ?? 0}</td>
                <td>{formatDate(release.release_date)}</td>
                <td>
                  {release.status === 'published' ? (
                    <div className="release-published-cont">
                      <span className="release-published">Published</span>
                    </div>
                  ) : (
                    <div className="release-draft-cont">
                      <span className="release-draft">Draft</span>
                    </div>
                  )}
                </td>
                <td className="release-delete-action">
                  <DeleteIcon onClick={(e) => handleDeleteClick(e, release)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DeletePopup
        isOpen={isDeletePopupOpen}
        title="Delete Release Note"
        fields={[{ label: 'Are you sure you want to delete this release note? Once deleted, it cannot be recovered.', type: 'text' }]}
        onSave={handleDelete}
        onClose={() => { setIsDeletePopupOpen(false); setSelectedItem(null); }}
      />
    </div>
  );
};

export default ReleaseNotes;